/**
 * i18n-Setup (Spec 14 / i18n.md): Deutsch ist Standard, Englisch wird
 * gleichwertig gepflegt. **Kein sichtbarer Text steht direkt im Code** —
 * alles laeuft ueber Translation-Keys in `snake_case`, hierarchisch gruppiert.
 *
 * Fehlende Keys sind Bugs: `i18n.test.ts` vergleicht beide Dateien.
 */
import { DEFAULT_LOCALE, LOCALES, type Locale } from '@tickethub/shared';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import de from './de.json';
import en from './en.json';

const STORAGE_KEY = 'tickethub.locale';

export function isLocale(value: string | null): value is Locale {
  return value !== null && (LOCALES as readonly string[]).includes(value);
}

/** Reihenfolge: gespeicherte Wahl → Browsersprache → Default (i18n.md). */
function detectLocale(): Locale {
  const stored = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY);
  if (isLocale(stored)) return stored;

  const browser = typeof navigator === 'undefined' ? '' : (navigator.language ?? '');
  const short = browser.split('-')[0] ?? '';
  return isLocale(short) ? short : DEFAULT_LOCALE;
}

export function setLocale(locale: Locale): void {
  localStorage.setItem(STORAGE_KEY, locale);
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
}

/**
 * Uebernimmt die im Konto hinterlegte Sprache (`users.locale`) — aber nur,
 * solange auf diesem Geraet noch niemand aktiv umgeschaltet hat. Sonst wuerde
 * jeder Seitenaufruf die Wahl des Benutzers ueberschreiben.
 *
 * Damit ist `users.locale` kein Feld, das nur geschrieben wird: es ist die
 * Voreinstellung fuer jedes neue Geraet.
 */
export function applyAccountLocale(locale: Locale): void {
  // Wert aus dem Netz — erst pruefen, dann anwenden.
  if (!isLocale(locale)) return;
  if (isLocale(localStorage.getItem(STORAGE_KEY))) return;
  if (i18n.language === locale) return;
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
}

void i18n.use(initReactI18next).init({
  resources: {
    de: { translation: de },
    en: { translation: en },
  },
  lng: detectLocale(),
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false }, // React escaped bereits
});

document.documentElement.lang = i18n.language;

export default i18n;
